<template>
    <v-container fluid>
        <v-row no-gutters>
            <v-col cols="3">
                <DateRangePicker
                    :begin="startDate"
                    :end="endDate"
                    @change-dateRange="onDateRangeChanged"
                    :hideDetails="true"
                    :type="'month'"
                    :formatDate="'month'"
                    dense
                    prefixDataCy="AccountingClientMonitoring-month"
                />
            </v-col>
            <v-col cols="2" class="pl-2" v-if="mode === 'InternalManagement'">
                <v-text-field
                    readonly
                    outlined
                    @click="openUserDialog"
                    hide-details
                    data-cy="AccountingClientMonitoring-collaborateur"
                    :model-value="selectedUser.firstname + ' ' + selectedUser.lastname"
                    density="compact"
                >
                </v-text-field>
            </v-col>
            <v-col cols="2" class="pl-2" v-else>
                <v-text-field
                    v-model="search"
                    label="Recherche"
                    outlined
                    hide-details
                    data-cy="AccountingClientMonitoring-search"
                    density="compact"
                    clearable
                    append-inner-icon="$search"
                />
            </v-col>
            <v-col cols="3" class="pl-2">
                <oss-company-selector
                    v-model="companiesSelected"
                    :multiple="true"
                    :items="allAvailableCompanies"
                    :label="'Société'"
                    :hideDetails="true"
                    :chipMaxLength="19"
                    :vSelectProps="{ bgColor: 'white' }"
                    data-cy="AccountingClientMonitoring-societe"
                />
            </v-col>
            <v-col cols="2" class="pl-2">
                <oss-company-activities-selector
                    v-model="codeNafSelected"
                    :inActivities="activities"
                    :multiple="true"
                    :hideDetails="true"
                />
            </v-col>
            <v-col cols="2" class="pl-2">
                <v-select
                    v-model="legalFormsSelected"
                    :items="legalForms"
                    chips
                    outlined
                    hide-details
                    label="Formes légales"
                    multiple
                    data-cy="AccountingClientMonitoring-formesLegales"
                    density="compact"
                />
            </v-col>
        </v-row>
        <v-row no-gutters class="d-flex align-center">
            <v-col cols="4">
                <v-btn-toggle
                    v-model="selectedView"
                    class="pt-1"
                    group
                    mandatory
                    rounded="xl"
                    v-if="isUserHasAccess"
                    density="compact"
                    variant="text"
                    color="primary"
                    style="overflow: hidden"
                >
                    <v-btn
                        value="production"
                        class="mx-0"
                        data-cy="AccountingClientMonitoring-production"
                        v-if="isUserAllowedToProduction"
                        style="border-top-left-radius: 16px; border-bottom-left-radius: 16px"
                    >
                        Production
                    </v-btn>
                    <v-btn
                        class="mx-0"
                        data-cy="AccountingClientMonitoring-taxes"
                        v-if="isUserAllowedToDeclaration"
                        :value="taxesTypeSelected"
                        style="border-radius: 0"
                    >
                        Déclarations
                    </v-btn>
                    <v-btn
                        value="finalization"
                        class="mx-0"
                        data-cy="AccountingClientMonitoring-finalization"
                        v-if="isUserAllowedToInvoices"
                        style="border-top-right-radius: 16px; border-bottom-right-radius: 16px"
                    >
                        Clôture
                    </v-btn>
                </v-btn-toggle>
            </v-col>
            <v-col cols="2" v-if="selectedView && selectedView?.includes('taxes')">
                <v-select
                    outlined
                    v-model="taxesTypeSelected"
                    :items="taxesTypes"
                    item-value="value"
                    required
                    hide-details="auto"
                    density="compact"
                    :item-title="(item) => item.key"
                />
            </v-col>
            <v-col cols="6">
                <v-row v-if="selectedView === 'production'" no-gutters>
                    <v-col class="d-flex justify-center align-start">
                        <v-checkbox
                            dense
                            v-model="showFECInfo"
                            label="FEC"
                            hide-details="auto"
                            data-cy="AccountingClientMonitoring-FEC"
                        />
                    </v-col>
                    <v-col
                        class="d-flex justify-center align-start"
                        v-for="option of options"
                        :key="option"
                    >
                        <v-checkbox
                            dense
                            v-model="optionsSelected"
                            :label="translateOption(option)"
                            hide-details="auto"
                            :data-cy="'AccountingClientMonitoring-' + translateOption(option)"
                            :value="option"
                        />
                    </v-col>
                </v-row>
                <v-row v-else-if="selectedView === 'taxesDeposits'" no-gutters>
                    <v-col
                        class="d-flex justify-center align-end"
                        v-for="tax of taxesDeposits"
                        :key="`${tax}-deposit`"
                    >
                        <v-checkbox
                            dense
                            v-model="taxesDepositsSelected"
                            :label="tax"
                            hide-details="auto"
                            :data-cy="'AccountingClientMonitoring-taxesSelected-' + tax"
                            :value="tax"
                        />
                    </v-col>
                </v-row>
                <v-row v-else-if="selectedView === 'taxesBalances'" no-gutters>
                    <v-col
                        class="d-flex justify-center align-end"
                        v-for="tax of taxesBalances"
                        :key="`${tax}-balance`"
                    >
                        <v-checkbox
                            dense
                            v-model="taxesBalancesSelected"
                            :label="tax"
                            hide-details="auto"
                            :value="tax"
                        />
                    </v-col>
                </v-row>
            </v-col>
            <v-col cols="12">
                <v-window v-model="mappedSelectedView" rounded="xl">
                    <v-window-item class="mt-1" value="production">
                        <v-row no-gutters>
                            <v-col cols="12">
                                <v-data-table-server
                                    :headers="headers"
                                    :items="filteredCompanies"
                                    v-model:page="pagination.page"
                                    v-model:sort-by="pagination.sortBy"
                                    v-model:items-per-page="pagination.rowsPerPage"
                                    :items-length="totalCompaniesCount"
                                    fixed-header
                                    :items-per-page-options="[10, 20, 30]"
                                    calculate-widths
                                    :loading="loading"
                                    :height="
                                        mode === 'InternalManagement'
                                            ? 'calc(100vh - 245px)'
                                            : 'calc(100vh - 300px)'
                                    "
                                    class="table"
                                >
                                    <template v-slot:item="props">
                                        <AccountingClientMonitoringProductionRow
                                            :props="props"
                                            :headers="headers"
                                            :itemValue="getValueForItem(props.item.id)"
                                            :taxesMonitoringItem="
                                                getTaxesMonitoringItem(props.item.id)
                                            "
                                            :optionsSelected="optionsSelected"
                                            :showFECInfo="showFECInfo"
                                        />
                                    </template>
                                </v-data-table-server>
                            </v-col>
                        </v-row>
                    </v-window-item>
                    <v-window-item class="mt-1" value="taxes">
                        <v-row no-gutters>
                            <v-col cols="12">
                                <v-data-table-server
                                    :headers="headers"
                                    :items="filteredCompanies"
                                    v-model:page="pagination.page"
                                    v-model:sort-by="pagination.sortBy"
                                    v-model:items-per-page="pagination.rowsPerPage"
                                    :items-length="totalCompaniesCount"
                                    fixed-header
                                    :items-per-page-options="[10, 20, 30]"
                                    calculate-widths
                                    :loading="loading"
                                    :height="
                                        mode === 'InternalManagement'
                                            ? 'calc(100vh - 245px)'
                                            : 'calc(100vh - 300px)'
                                    "
                                    class="table"
                                >
                                    <template v-slot:item="props">
                                        <AccountingClientMonitoringTaxesRow
                                            :props="props"
                                            :headers="headers"
                                            :itemValue="getValueForItem(props.item.id)"
                                            :taxesMonitoringItem="
                                                getTaxesMonitoringItem(props.item.id)
                                            "
                                            :taxesSelected="taxesSelected"
                                            :taxType="selectedView"
                                            :hideName="false"
                                        />
                                    </template>
                                </v-data-table-server>
                            </v-col>
                        </v-row>
                    </v-window-item>
                    <v-window-item class="mt-1" value="finalization">
                        <v-row no-gutters>
                            <v-col cols="12">
                                <v-data-table-server
                                    :headers="headers"
                                    :items="filteredCompanies"
                                    v-model:page="pagination.page"
                                    v-model:sort-by="pagination.sortBy"
                                    v-model:items-per-page="pagination.rowsPerPage"
                                    :items-length="totalCompaniesCount"
                                    fixed-header
                                    :items-per-page-options="[10, 20, 30]"
                                    calculate-widths
                                    :loading="loading"
                                    :height="
                                        mode === 'InternalManagement'
                                            ? 'calc(100vh - 245px)'
                                            : 'calc(100vh - 300px)'
                                    "
                                    class="table"
                                >
                                    <template v-slot:item="props">
                                        <AccountingClientMonitoringClosureRow
                                            :props="props"
                                            :headers="headers"
                                            :auditReportMonitoring="auditReportMonitoring"
                                            :loading="loading"
                                            :hideName="false"
                                        />
                                    </template>
                                </v-data-table-server>
                            </v-col>
                        </v-row>
                    </v-window-item>
                </v-window>
            </v-col>
        </v-row>
        <!-- <oss-dialog v-model="actionDialog" :title="'Que voulez-vous faire ?'" max-width="800px">
            <template v-slot:actions>
                <v-row no-gutters>
                    <v-col cols="6" class="pr-2">
                        <v-btn
                            class="rounded-lg gradient"
                            rounded
                             size="small"
                            block
                             size=“x-large”
                            elevation="0"

                            @click="redirectionAction()"
                            data-cy="AccountingClientMonitoring-redirection"
                            >Aller sur la page</v-btn
                        >
                    </v-col>
                    <v-col cols="6" class="pl-2">
                        <v-btn
                            class="rounded-lg gradient"
                            rounded
                             size="small"
                            block
                             size=“x-large”
                            elevation="0"

                            @click="createSet"
                            >Ignorer</v-btn
                        >
                    </v-col>
                </v-row>
            </template>
        </oss-dialog> -->
        <oss-dialog v-model="userDialog" :title="'Collaborateur'" max-width="800px">
            <select-user
                class="px-1"
                v-model="selectedUser"
                :companyId="$store.state.user.company.id"
                :type="'NATURAL_PERSON'"
                :withEmail="true"
                @user-selected="userDialog = false"
            />
        </oss-dialog>
    </v-container>
</template>

<script lang="ts">
import moment from "moment-timezone";
import colors from "vuetify/lib/util/colors";
import gql from "graphql-tag";
import { Apollo } from "@/decorators";
import { User } from "../../store/store";

import USER_DETAILS from "../../graphql/user/UserDetails.gql";
import AUDIT_MONITORING from "../../graphql/audit/Monitoring.gql";
import TAXES_MONITORING from "../../graphql/audit/TaxesMonitoring.gql";
import AUDIT_REPORT_MONITORING from "../../graphql/audit/AuditReportMonitoring.gql";
import COMPANIES from "../../graphql/company/CompaniesFilter.gql";

import DateRangePicker from "../ossComponents/ossDateRangePicker.vue";
import OssCompanyActivitiesSelector from "../ossComponents/ossCompanyActivitiesSelector.vue";
import OssCompanySelector from "../ossComponents/ossCompanySelector.vue";
import SelectUser from "../user/SelectUser.vue";
import { setCurrentCompany } from "../../services/userServices";
import { checkAccessRights, OfferAccessRight } from "@/services/accessRightServices";

import { Offer_type } from "@/types/offer.types";

import AccountingClientMonitoringProductionRow from "./AccountingClientMonitoringProductionRow.vue";
import AccountingClientMonitoringTaxesRow from "./AccountingClientMonitoringTaxesRow.vue";
import AccountingClientMonitoringClosureRow from "./AccountingClientMonitoringClosureRow.vue";
import AccountingClientMonitoringServices from "@/services/accountingClientMonitoringServices";
import { Component, Vue, toNative } from "vue-facing-decorator";
import { Prop } from "vue-facing-decorator";
import { Watch } from "vue-facing-decorator";

const checkMonitoringKey = "c1b6011ebadc10242ac120002";

@Component({
    components: {
        DateRangePicker,
        OssCompanyActivitiesSelector,
        OssCompanySelector,
        SelectUser,
        AccountingClientMonitoringProductionRow,
        AccountingClientMonitoringTaxesRow,
        AccountingClientMonitoringClosureRow,
    },
})
export class AccountingClientMonitoring extends Vue {
    @Prop({ default: "InternalManagement" }) mode: string;

    activeTab: string = "production";

    selectedView: string = "production";

    get mappedSelectedView() {
        if (["taxesBalances", "taxesDeposits"].includes(this.selectedView)) {
            return "taxes";
        }
        return this.selectedView;
    }

    startDate = "";
    endDate = "";

    userDialog = false;

    options = ["EXPENSE", "SALES", "PAY_SLIP", "BANK_TRANSACTION"];
    optionsSelected = ["EXPENSE", "SALES", "PAY_SLIP", "BANK_TRANSACTION"];

    taxesDeposits = ["TVA", "IS", "CVAE"];
    taxesBalances = ["IS", "CVAE"];
    taxesDepositsSelected = ["TVA", "IS", "CVAE"];
    taxesBalancesSelected = ["IS", "CVAE"];

    get taxesSelected() {
        if (this.taxesTypeSelected === "taxesDeposits") {
            return this.taxesDepositsSelected;
        } else if (this.taxesTypeSelected === "taxesBalances") {
            return this.taxesBalancesSelected;
        }
        return [];
    }

    taxesTypes = [
        { key: "Acomptes", value: "taxesDeposits" },
        { key: "Soldes", value: "taxesBalances" },
    ];

    taxesTypeSelected = "taxesDeposits";

    showFECInfo: boolean = false;

    codeNafSelected = [];

    legalFormsSelected = [];

    eagerArray = [];

    pagination: any = {
        page: 1,
        rowsPerPage: 10,
        sortBy: [{ key: "name", order: "desc" }],
    };

    search: string = "";

    @Watch("search")
    onSearchChanged() {
        this.pagination.page = 1;
    }

    @Watch("taxesTypeSelected")
    onTaxesTypeChanged() {
        this.selectedView = this.taxesTypeSelected;
    }

    get legalForms() {
        const legalForms = [];
        for (const company of this.allAvailableCompanies) {
            if (!company.legalForm) {
                continue;
            }
            if (legalForms.indexOf(company.legalForm) === -1) {
                legalForms.push(company.legalForm);
            }
        }
        return legalForms;
    }

    get activities() {
        const activities = [];
        for (const company of this.allAvailableCompanies) {
            if (!company.activities?.length) {
                continue;
            }
            if (!activities.find((a) => a.id === company.activities[0].id)) {
                const activity = company.activities[0];
                if (activity) {
                    activity.count = 1;
                    activities.push(activity);
                }
            }
        }
        return activities;
    }

    selectedUser = {
        email: "",
        firstname: "",
        id: "",
        lastname: "",
        type: "",
    };

    get headers() {
        const h = AccountingClientMonitoringServices.getHeaders(
            this.showFECInfo,
            this.selectedView,
            true,
            this.startDate,
            this.endDate,
        );
        return h;
    }

    beforeMount() {
        this.startDate = moment().add(-11, "months").startOf("month").format("YYYY-MM");
        this.endDate = moment().endOf("month").format("YYYY-MM");
        this.selectedUser = this.$store.state.user;
    }

    get currentUser(): User {
        return this.$store.state.user;
    }

    get isAccountingEmployee() {
        return this.currentUser.isAccountingEmployee;
    }

    openUserDialog() {
        if (!this.isAccountingEmployee) {
            this.userDialog = true;
        }
    }

    get availableCompaniesId() {
        return this.allAvailableCompanies.map((x) => x.id);
    }

    // get red() {
    //     return colors.red.accent1;
    // }

    // get green() {
    //     return "green";
    // }

    // get blue() {
    //     return colors.blue.lighten1;
    // }

    // get orange() {
    //     return colors.orange.lighten1;
    // }

    // get white() {
    //     return colors.grey.lighten4;
    // }

    // get grey() {
    //     return colors.grey.lighten2;
    // }

    get offerType() {
        return this.$store.state.offerType;
    }

    get isUserHasAccess() {
        return this.$store.getters.isUserHasAccess();
    }

    get isUserAllowedToProduction() {
        // switch (this.offerType) {
        // case Offer_type.ACCOUNTING_BASIC:
        // case Offer_type.ACCOUNTING_ESSENTIAL:
        // case Offer_type.ACCOUNTING_PREMIUM:
        // case Offer_type.BUSINESS_PREMIUM:
        // case Offer_type.TRAINING:
        // if (this.isUserHasAccess) {
        // return true;
        // }
        // return false;
        // default:
        // return false;
        // }
        return OfferAccessRight.checkOfferAccessRight("isAllowedToProduction", true);
    }

    get isUserAllowedToDeclaration() {
        // switch (this.offerType) {
        // case Offer_type.ACCOUNTING_BASIC:
        // case Offer_type.ACCOUNTING_ESSENTIAL:
        // case Offer_type.ACCOUNTING_PREMIUM:
        // case Offer_type.BUSINESS_PREMIUM:
        // case Offer_type.TRAINING:
        // if (this.isUserHasAccess) {
        // return true;
        // }
        // return false;
        // default:
        // return false;
        // }
        return checkAccessRights(
            this.currentUser,
            "declaration",
            "read",
            true,
            "isAllowedToDeclaration",
            true,
        );
    }

    get isUserAllowedToInvoices() {
        // switch (this.offerType) {
        // case Offer_type.ACCOUNTING_BASIC:
        // case Offer_type.ACCOUNTING_ESSENTIAL:
        // case Offer_type.ACCOUNTING_PREMIUM:
        // case Offer_type.BUSINESS_PREMIUM:
        // case Offer_type.TRAINING:
        // if (this.isUserHasAccess) {
        // return true;
        // }
        // return false;
        // default:
        // return false;
        // }
        return checkAccessRights(
            this.currentUser,
            "salesInvoice",
            "read",
            true,
            "isAllowedToInvoice",
            true,
        );
    }

    get selectedCompanies() {
        if (this.companiesSelected.length) {
            return this.companiesSelected;
        }
        return this.companies;
    }

    get filteredCompanies() {
        if (this.search) {
            const lSearch = this.search;
            const filteredCompanies = [];

            for (const company of this.allAvailableCompanies) {
                if (company.name?.toLowerCase().indexOf(lSearch) !== -1) {
                    filteredCompanies.push(company);
                }
            }
            return filteredCompanies;
        }
        return this.selectedCompanies;
    }

    // taxesDepositsSchedule = {
    //     CVAE: ["06", "09"],
    //     IS: ["03", "06", "09", "12"],
    //     TVAQ: ["01", "04", "07", "10"],
    //     TVAS: ["07", "12"],
    // };

    // taxesDepositsDico = {
    //     "1329AC": "Acompte de CVAE",
    //     "2571": "Acompte d'IS",
    //     "3310CA3": "TVA",
    //     "3514": "Acompte de TVA",
    // };

    // taxesBalancesDico = {
    //     "1329DEF": "Solde de CVAE",
    //     "2572": "Solde d'IS",
    //     "3517SCA12": "Solde de TVA",
    // };

    companiesSelected = [];

    actionType = null;
    actionSelectedCompany = null;
    actionDate = null;
    actionDialog = false;

    @Watch("filteredCompanies")
    @Watch("startDate")
    @Watch("endDate")
    resetEagerArray() {
        this.eagerArray = [];
    }

    @Apollo({
        query: AUDIT_MONITORING,
        variables() {
            return {
                companiesId: this.filteredCompanies.map((x) => x.id),
                startDate: moment(this.startDate).startOf("month").format("YYYY-MM-DD"),
                endDate: moment(this.endDate + "-01")
                    .endOf("month")
                    .format("YYYY-MM-DD"),
                // endDate: this.endDate + "-31"
            };
        },
        debounce: 500,
        skip() {
            return (
                !this.startDate ||
                !this.endDate ||
                !this.filteredCompanies.length ||
                this.selectedView !== "production" ||
                this.eagerArray?.includes("production")
            );
        },
        specificResult({ data, loading, networkStatus }: any) {
            this.loadingDocument = loading;
            if (!loading) {
                if (data && data.auditMonitoring && data.auditMonitoring.monitoring) {
                    this.values = data.auditMonitoring.monitoring;
                } else {
                    this.values = {};
                }
                this.eagerArray.push("production");
            }
        },
    })
    values = {};

    get loading() {
        if (this.selectedView === "production" && this.$apollo.queries.values) {
            return this.$apollo.queries.values.loading;
        }
        if (this.selectedView?.includes("taxes") && this.$apollo.queries.taxesMonitoring) {
            return this.$apollo.queries.taxesMonitoring.loading;
        }
        if (this.selectedView === "finalization" && this.$apollo.queries.auditReportMonitoring) {
            return this.$apollo.queries.auditReportMonitoring.loading;
        }
        return false;
    }

    @Apollo({
        query: AUDIT_REPORT_MONITORING,
        variables() {
            return {
                companiesId: this.filteredCompanies.map((x) => x.id),
                startDate: moment(this.startDate).startOf("month").format("YYYY-MM-DD"),
                endDate: moment(this.endDate + "-01")
                    .endOf("month")
                    .format("YYYY-MM-DD"),
                // endDate: this.endDate + "-31"
            };
        },
        debounce: 500,
        skip() {
            return (
                !this.startDate ||
                !this.endDate ||
                !this.filteredCompanies.length ||
                this.selectedView !== "finalization" ||
                this.eagerArray?.includes("finalization")
            );
        },
        specificResult({ data, loading, networkStatus }: any) {
            this.loadingDocument = loading;
            if (!loading) {
                if (data?.auditReportMonitoring?.auditReportMonitoring) {
                    this.auditReportMonitoring = JSON.parse(
                        JSON.stringify(data.auditReportMonitoring.auditReportMonitoring),
                    );
                } else {
                    this.auditReportMonitoring = {};
                }
                this.eagerArray.push("finalization");
            }
        },
    })
    auditReportMonitoring = {};

    @Apollo({
        query: TAXES_MONITORING,
        variables() {
            return {
                companiesId: this.filteredCompanies.map((x) => x.id),
                startDate: moment(this.startDate).startOf("month").format("YYYY-MM-DD"),
                endDate: moment(this.endDate + "-01")
                    .endOf("month")
                    .format("YYYY-MM-DD"),
                // endDate: this.endDate + "-31"
            };
        },
        debounce: 500,
        skip() {
            return (
                !this.startDate ||
                !this.endDate ||
                !this.filteredCompanies.length ||
                !this.selectedView?.includes("taxes") ||
                this.eagerArray?.includes("taxes")
            );
        },
        specificResult({ data, loading, networkStatus }: any) {
            this.loadingDocument = loading;
            if (!loading) {
                if (data?.taxesMonitoring?.taxesMonitoring) {
                    this.taxesMonitoring = data.taxesMonitoring.taxesMonitoring;
                } else {
                    this.taxesMonitoring = {};
                }
                this.eagerArray.push("taxes");
            }
        },
    })
    taxesMonitoring = {};

    @Apollo({
        query: USER_DETAILS,
        variables() {
            return {
                id: this.selectedUser.id,
            };
        },
        skip() {
            return !this.selectedUser || !this.selectedUser.id;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data && data.user && data.user.allAvailableCompanies) {
                    this.allAvailableCompanies = data.user.allAvailableCompanies;
                } else {
                    this.allAvailableCompanies = [];
                }
            }
        },
    })
    allAvailableCompanies = [];

    @Apollo({
        query: COMPANIES,
        variables() {
            const where = {
                id_in: this.availableCompaniesId,
                activities_some: undefined,
                legalForm_in: undefined,
            };
            if (this.codeNafSelected.length) {
                where.activities_some = { id_in: this.codeNafSelected.map((x) => x.id) };
            }
            if (this.legalFormsSelected.length) {
                where.legalForm_in = this.legalFormsSelected;
            }
            return {
                orderBy: "name_ASC",
                where: where,
                skip: (this.pagination.page - 1) * this.pagination.rowsPerPage,
                first: this.pagination.rowsPerPage,
            };
        },
        skip() {
            return !this.availableCompaniesId.length;
        },
        specificResult({ data, loading, networkStatus }: any) {
            this.loadingDocument = loading;
            if (!loading) {
                if (data && data.companies && data.companies.length) {
                    this.companies = data.companies;
                    this.totalCompaniesCount = data.companiesConnection.aggregate.count;
                } else {
                    this.companies = [];
                    this.totalCompaniesCount = 0;
                }
            }
        },
    })
    companies = [];
    totalCompaniesCount = 0;

    // getValues(id) {
    //     if (!this.values) {
    //         return [];
    //     }
    //     const values = this.values[id];
    //     return values;
    // }

    // getDepositDeclarationInfos(company, tax, date) {
    //     if (
    //         this.selectedView === "taxesDeposits" &&
    //         this.taxesMonitoring &&
    //         this.taxesMonitoring[company.id] &&
    //         this.taxesMonitoring[company.id][tax] &&
    //         this.taxesMonitoring[company.id][tax][date]
    //     ) {
    //         return `${
    //             this.taxesDepositsDico[this.taxesMonitoring[company.id][tax][date].cerfaNumber]
    //         } - ${moment(date).format("MM/YYYY")} - ${
    //             this.taxesMonitoring[company.id][tax][date].status === "SENT" ? "Validé" : "Édition"
    //         }`;
    //     } else {
    //         if (tax === "IS") {
    //             if (this.taxesDepositsSchedule[tax].includes(date.substring(5, 7))) {
    //                 return `Déclaration d'acompte d'IS du 15/${moment(date).format(
    //                     "MM/YYYY",
    //                 )} manquante`;
    //             } else {
    //                 return `Pas de déclaration d'acompte d'IS attendue au mois de ${moment(
    //                     date,
    //                 ).format("MM/YYYY")}`;
    //             }
    //         } else if (tax === "TVA") {
    //             if (
    //                 (company.vatSystem === "Quartely" &&
    //                     this.taxesDepositsSchedule["TVAQ"]?.includes(date.substring(5, 7))) ||
    //                 (company.vatSystem === "Simplified" &&
    //                     this.taxesDepositsSchedule["TVAS"]?.includes(date.substring(5, 7))) ||
    //                 company.vatSystem === "Normal"
    //             ) {
    //                 if (company.vatDeclarationDay) {
    //                     return `Déclaration de TVA du ${company.vatDeclarationDay}/${moment(
    //                         date,
    //                     ).format("MM/YYYY")} manquante`;
    //                 } else {
    //                     return `Déclaration de TVA du mois de ${moment(date).format(
    //                         "MM/YYYY",
    //                     )} manquante`;
    //                 }
    //             } else {
    //                 return `Pas de déclaration de TVA attendue au mois de ${moment(date).format(
    //                     "MM/YYYY",
    //                 )}`;
    //             }
    //         } else if (tax === "CVAE") {
    //             if (this.taxesDepositsSchedule[tax]?.includes(date.substring(5, 7))) {
    //                 return `Déclaration d'acompte de CVAE du 15/${moment(date).format(
    //                     "MM/YYYY",
    //                 )} manquante`;
    //             } else {
    //                 return `Pas de déclaration d'acompte de CVAE attendue au mois de ${moment(
    //                     date,
    //                 ).format("MM/YYYY")}`;
    //             }
    //         }
    //     }
    // }

    // getBalanceDeclarationInfos(company, tax, date) {
    //     // same logic as getColor function
    //     if (tax === "CVAE") {
    //         if (date.substring(5, 7) !== "05") {
    //             return `Pas de déclaration de solde de CVAE attendue au mois de ${moment(
    //                 date,
    //             ).format("MM/YYYY")}`;
    //         }
    //         if (!company.accountingPeriods.length) {
    //             return `Pas de déclaration de solde de CVAE attendue au mois de ${moment(
    //                 date,
    //             ).format("MM/YYYY")}`;
    //         }
    //         if (
    //             this.taxesMonitoring &&
    //             this.taxesMonitoring[company.id] &&
    //             this.taxesMonitoring[company.id][tax]
    //         ) {
    //             const declarations = this.taxesMonitoring[company.id][tax];
    //             const theoreticalClosingDate = `${parseFloat(date.slice(0, 4)) - 1}-12`;
    //             if (declarations[theoreticalClosingDate]) {
    //                 const declaration = declarations[theoreticalClosingDate];
    //                 if (Object.keys(this.taxesBalancesDico)?.includes(declaration.cerfaNumber)) {
    //                     if (declaration.status === "SENT") {
    //                         return `Solde CVAE - ${moment(date).format("MM/YYYY")} - Validé`;
    //                     }
    //                     if (declaration.status === "EDITION") {
    //                         return `Solde CVAE - ${moment(date).format("MM/YYYY")} - Édition`;
    //                     }
    //                 }
    //             }
    //         }
    //         return `Déclaration de solde de CVAE de ${moment(date).format("MM/YYYY")} manquante`;
    //     }
    //     if (tax === "IS") {
    //         if (company.taxSystemType !== "IS") {
    //             return `Pas de déclaration de solde d'IS attendue au mois de ${moment(date).format(
    //                 "MM/YYYY",
    //             )}`;
    //         }
    //         if (
    //             this.getCorporateTaxBalanceDueDate(company.accountingPeriods, new Date(date)) !==
    //             date
    //         ) {
    //             return `Pas de déclaration de solde d'IS attendue au mois de ${moment(date).format(
    //                 "MM/YYYY",
    //             )}`;
    //         }
    //         const theoreticalClosingDate =
    //             date.slice(-2) === "05"
    //                 ? moment(new Date(date)).subtract(5, "months").format("YYYY-MM")
    //                 : moment(new Date(date)).subtract(4, "months").format("YYYY-MM");
    //         if (
    //             this.taxesMonitoring &&
    //             this.taxesMonitoring[company.id] &&
    //             this.taxesMonitoring[company.id][tax]
    //         ) {
    //             const declarations = this.taxesMonitoring[company.id][tax];
    //             if (declarations[theoreticalClosingDate]) {
    //                 const declaration = declarations[theoreticalClosingDate];
    //                 if (Object.keys(this.taxesBalancesDico)?.includes(declaration.cerfaNumber)) {
    //                     if (declaration.status === "SENT") {
    //                         return `Solde IS - ${moment(date).format("MM/YYYY")} - Validé`;
    //                     }
    //                     if (declaration.status === "EDITION") {
    //                         return `Solde IS - ${moment(date).format("MM/YYYY")} - Édition`;
    //                     }
    //                 }
    //             }
    //         }
    //         return `Déclaration de solde d'IS du 15/${moment(date).format("MM/YYYY")} manquante`;
    //     }
    // }

    // getBundleColor(company) {
    //     if (!this.auditReportMonitoring[company.id]?.bundle) {
    //         return this.grey;
    //     }

    //     if (
    //         this.auditReportMonitoring &&
    //         this.auditReportMonitoring[company.id] &&
    //         this.auditReportMonitoring[company.id].bundle
    //     ) {
    //         switch (this.auditReportMonitoring[company.id].bundle.status) {
    //             case "EDITION":
    //                 return this.orange;
    //             case "SENT":
    //                 switch (this.auditReportMonitoring[company.id].bundle.mtaeStatus) {
    //                     case "Déclaration acceptée":
    //                         return this.green;
    //                     default:
    //                         return this.blue;
    //                 }
    //             case "ERROR":
    //                 return this.red;
    //             default:
    //                 return this.grey;
    //         }
    //     }
    // }

    // getBundleStatus(company) {
    //     if (!this.auditReportMonitoring[company.id]?.bundle) {
    //         return "Aucune liasse";
    //     }

    //     if (
    //         this.auditReportMonitoring &&
    //         this.auditReportMonitoring[company.id] &&
    //         this.auditReportMonitoring[company.id].bundle
    //     ) {
    //         switch (this.auditReportMonitoring[company.id].bundle.status) {
    //             case "EDITION":
    //                 return "Liasse en cours d'édition";
    //             case "SENT":
    //                 switch (this.auditReportMonitoring[company.id].bundle.mtaeStatus) {
    //                     case "Déclaration acceptée":
    //                         return "Liasse acceptée par l'administration fiscale";
    //                     case "OK":
    //                         return "Liasse envoyée à l'administration fiscale";
    //                     default:
    //                         return this.auditReportMonitoring[company.id].bundle.mtaeStatus;
    //                 }
    //             case "ERROR":
    //                 switch (this.auditReportMonitoring[company.id].bundle.mtaeStatus) {
    //                     case "Déclaration refusée":
    //                         return "Liasse refusée par l'administration fiscale";
    //                     default:
    //                         return "Erreur";
    //                 }
    //             default:
    //                 return "Inconnu";
    //         }
    //     }
    // }

    // getColor(company, tax, date) {
    //     if (this.selectedView === "production") {
    //         if (tax === "PAY_SLIP" && company.profile && !company.profile.createPaySlip) {
    //             return this.white;
    //         }
    //         if (tax === "VAT" && company.vatSystem && company.vatSystem === "NotSubjectTo") {
    //             return this.white;
    //         }
    //         if (
    //             this.values &&
    //             this.values[company.id] &&
    //             this.values[company.id][tax] &&
    //             this.values[company.id][tax][date]
    //         ) {
    //             if (this.values[company.id][tax][date].pendingValidation) {
    //                 return this.orange;
    //             }
    //             if (this.values[company.id][tax][date].auditCheckResult) {
    //                 return this.grey;
    //             }
    //             return this.green;
    //         }
    //         return this.red;
    //     } else if (this.selectedView === "taxesDeposits") {
    //         if (
    //             (tax === "CVAE" &&
    //                 !this.taxesDepositsSchedule[tax]?.includes(date.substring(5, 7))) ||
    //             (tax === "IS" && company.taxSystemType !== "IS") ||
    //             (tax === "IS" &&
    //                 !this.taxesDepositsSchedule[tax]?.includes(date.substring(5, 7))) ||
    //             (tax === "TVA" && company.vatSystem && company.vatSystem === "NotSubjectTo") ||
    //             (tax === "TVA" &&
    //                 company.vatSystem &&
    //                 company.vatSystem === "Quartely" &&
    //                 !this.taxesDepositsSchedule["TVAQ"]?.includes(date.substring(5, 7))) ||
    //             (tax === "TVA" &&
    //                 company.vatSystem &&
    //                 company.vatSystem === "Simplified" &&
    //                 !this.taxesDepositsSchedule["TVAS"]?.includes(date.substring(5, 7)))
    //         ) {
    //             return this.white;
    //         }
    //         if (
    //             this.taxesMonitoring &&
    //             this.taxesMonitoring[company.id] &&
    //             this.taxesMonitoring[company.id][tax] &&
    //             this.taxesMonitoring[company.id][tax][date] &&
    //             this.taxesMonitoring[company.id][tax][date].cerfaNumber &&
    //             this.taxesMonitoring[company.id][tax][date].status
    //         ) {
    //             const declaration = this.taxesMonitoring[company.id][tax][date];
    //             if (Object.keys(this.taxesDepositsDico)?.includes(declaration.cerfaNumber)) {
    //                 if (declaration.status === "SENT") {
    //                     return this.green;
    //                 }
    //                 if (declaration.status === "EDITION") {
    //                     return this.orange;
    //                 }
    //             }
    //         }
    //         return this.red;
    //     }
    //     // same logic as getBalanceDeclarationInfos function
    //     else if (this.selectedView === "taxesBalances") {
    //         if (tax === "CVAE") {
    //             if (date.substring(5, 7) !== "05") {
    //                 return this.white;
    //             }
    //             if (!company.accountingPeriods.length) {
    //                 return this.white;
    //             }
    //             if (
    //                 this.taxesMonitoring &&
    //                 this.taxesMonitoring[company.id] &&
    //                 this.taxesMonitoring[company.id][tax]
    //             ) {
    //                 const declarations = this.taxesMonitoring[company.id][tax];
    //                 const theoreticalClosingDate = `${parseFloat(date.slice(0, 4)) - 1}-12`;
    //                 if (declarations[theoreticalClosingDate]) {
    //                     const declaration = declarations[theoreticalClosingDate];
    //                     if (
    //                         Object.keys(this.taxesBalancesDico)?.includes(declaration.cerfaNumber)
    //                     ) {
    //                         if (declaration.status === "SENT") {
    //                             return this.green;
    //                         }
    //                         if (declaration.status === "EDITION") {
    //                             return this.orange;
    //                         }
    //                     }
    //                 }
    //             }
    //         }
    //         if (tax === "IS") {
    //             if (company.taxSystemType !== "IS") {
    //                 return this.white;
    //             }
    //             if (
    //                 this.getCorporateTaxBalanceDueDate(
    //                     company.accountingPeriods,
    //                     new Date(date),
    //                 ) !== date
    //             ) {
    //                 return this.white;
    //             }
    //             const theoreticalClosingDate =
    //                 date.slice(-2) === "05"
    //                     ? moment(new Date(date)).subtract(5, "months").format("YYYY-MM")
    //                     : moment(new Date(date)).subtract(4, "months").format("YYYY-MM");
    //             if (
    //                 this.taxesMonitoring &&
    //                 this.taxesMonitoring[company.id] &&
    //                 this.taxesMonitoring[company.id][tax]
    //             ) {
    //                 const declarations = this.taxesMonitoring[company.id][tax];
    //                 if (declarations[theoreticalClosingDate]) {
    //                     const declaration = declarations[theoreticalClosingDate];
    //                     if (Object.keys(this.taxesBalancesDico).includes(declaration.cerfaNumber)) {
    //                         if (declaration.status === "SENT") {
    //                             return this.green;
    //                         }
    //                         if (declaration.status === "EDITION") {
    //                             return this.orange;
    //                         }
    //                     }
    //                 }
    //             }
    //         }
    //         return this.red;
    //     }
    // }

    // getCorporateTaxBalanceDueDate(
    //     accountingPeriods: { id: string; start: Date; end: Date }[],
    //     currentDate: Date,
    // ): string {
    //     if (accountingPeriods) {
    //         const pastEndDates = accountingPeriods
    //             .map((period) => new Date(period.end))
    //             .filter((endDate) => endDate < currentDate)
    //             .sort((a, b) => a.getTime() - b.getTime());
    //         if (pastEndDates) {
    //             const mostRecentClosingDate = pastEndDates.pop();
    //             if (moment(mostRecentClosingDate).format("MM-DD") === "12-31") {
    //                 return moment(mostRecentClosingDate).add(5, "months").format("YYYY-MM");
    //             }
    //             return moment(mostRecentClosingDate).add(4, "months").format("YYYY-MM");
    //         }
    //     }
    // }

    // getClick(company, tax, date, path: string = null) {
    //     this.actionSelectedCompany = company;
    //     this.actionType = tax;
    //     this.actionDate = date;
    //     if (this.selectedView === "production") {
    //         if (
    //             this.values &&
    //             this.values[company.id] &&
    //             this.values[company.id][tax] &&
    //             this.values[company.id][tax][date]
    //         ) {
    //             this.redirectionAction(!!this.values[company.id][tax][date].pendingValidation);
    //             return;
    //         }
    //     } else if (this.selectedView?.includes("taxes")) {
    //         if (
    //             this.taxesMonitoring &&
    //             this.taxesMonitoring[company.id] &&
    //             this.taxesMonitoring[company.id][tax] &&
    //             this.taxesMonitoring[company.id][tax][date] &&
    //             this.taxesMonitoring[company.id][tax][date].id
    //         ) {
    //             this.redirectionAction(false, this.taxesMonitoring[company.id][tax][date].id);
    //             return;
    //         }
    //     } else if (this.selectedView === "finalization" && path.length) {
    //         this.redirectionAction(false, null, path);
    //         return;
    //     }

    //     this.actionDialog = true;
    // }

    // getPreviousFecStatus(item) {
    //     if (this.values && this.values[item.id] && this.values[item.id].fecLastYear) {
    //         return this.$t("accountingItemImport.status." + this.values[item.id].fecLastYear);
    //     }
    //     return "Aucun FEC l'année précédente";
    // }

    // getFECStatus(item) {
    //     if (this.values && this.values[item.id] && this.values[item.id].fecFirstYear) {
    //         return "$oss-circle-check";
    //     }
    //     return "$oss-circle-uncheck";
    // }

    // getFECColor(item) {
    //     if (this.values && this.values[item.id] && this.values[item.id].fecFirstYear) {
    //         return this.green;
    //     }
    //     return this.grey;
    // }

    // haveFec(item) {
    //     // if (this.values && this.values[item.id] && this.values[item.id].fecLastYear) {
    //     //     return true;
    //     // }
    //     if (this.values && this.values[item.id] && this.values[item.id].fecFirstYear) {
    //         return true;
    //     }
    //     return false;
    // }

    // async openDashboard(company) {
    //     this.actionSelectedCompany = company;
    //     this.actionType = "DASHBOARD";
    //     this.actionDate = "";

    //     const selectedCompany = this.allAvailableCompanies.find(
    //         (x) => x.id === this.actionSelectedCompany.id,
    //     );

    //     await setCurrentCompany(this.$apollo, this.$store, selectedCompany, undefined, true);
    //     // this.$router.push({ name: "dashboard" });
    //     this.$router.push({ name: "companyStatusBoard" });
    // }

    // async redirectionAction(
    //     addStatusfilter: boolean = false,
    //     declarationId: string = null,
    //     path: string = null,
    // ) {
    //     const selectedCompany = this.allAvailableCompanies.find(
    //         (x) => x.id === this.actionSelectedCompany.id,
    //     );
    //     await setCurrentCompany(this.$apollo, this.$store, selectedCompany, undefined, true);

    //     if (this.selectedView === "production") {
    //         const startDate = this.actionDate + "-01";
    //         const endDate = moment(startDate).endOf("month").format("YYYY-MM-DD");

    //         if (this.actionType === "BANK_TRANSACTION") {
    //             this.$store.commit("clearBankTransactionFilter");
    //             if (addStatusfilter) {
    //                 this.$store.commit(
    //                     "updateBankTransactionReconciliationStatus",
    //                     "NOT_JUSTIFIED",
    //                 );
    //             }
    //             this.$store.commit("updateBankTransactionBeginDate", startDate);
    //             this.$store.commit("updateBankTransactionEndDate", endDate);

    //             this.$router.push({ name: "bank" });
    //         } else if (this.actionType === "VAT") {
    //             this.$router.push({ name: "taxForms" });
    //         } else {
    //             this.$store.commit("clearDocumentFilter");
    //             this.$store.commit("updateDocumentType", this.actionType);
    //             if (addStatusfilter) {
    //                 this.$store.commit("updateDocumentStatus", "ANALYSED");
    //             }
    //             this.$store.commit("updateDocumentBeginDate", startDate);
    //             this.$store.commit("updateDocumentEndDate", endDate);
    //             this.$router.push({ name: "documents" });
    //         }
    //     } else if (this.selectedView?.includes("taxes")) {
    //         if (declarationId) {
    //             this.$router.push({ name: "declaration", params: { decId: declarationId } });
    //         } else {
    //             this.$router.push({ name: "taxForms" });
    //         }
    //     } else if (this.selectedView === "finalization" && path) {
    //         this.$router.push({ name: path });
    //     }
    // }

    // async createSet() {
    //     let data = {};
    //     if (this.values && this.values[this.actionSelectedCompany.id]) {
    //         data = this.values[this.actionSelectedCompany.id];
    //     }
    //     if (!data[this.actionType]) {
    //         data[this.actionType] = {};
    //     }
    //     data[this.actionType][this.actionDate] = { auditCheckResult: true };

    //     await this.$apollo.mutate({
    //         mutation: gql`
    //             mutation UpsertSetMonitoring(
    //                 $key: String!
    //                 $companyId: ID!
    //                 $data: Json
    //                 $status: AuditCheckResultStatus!
    //             ) {
    //                 upsertAuditCheckResult(
    //                     where: { key: $key }
    //                     create: {
    //                         auditCheck: { connect: { key: checkMonitoringKey } }
    //                         company: { connect: { id: $companyId } }
    //                         key: $key
    //                         data: $data
    //                         status: $status
    //                     }
    //                     update: { data: $data }
    //                 ) {
    //                     id
    //                 }
    //             }
    //         `,
    //         variables: {
    //             key: `${checkMonitoringKey}-${this.actionSelectedCompany.id}`,
    //             companyId: this.actionSelectedCompany.id,
    //             data: data,
    //             status: "UNKNOWN",
    //         },
    //     });
    //     this.values[this.actionSelectedCompany.id] = data;
    //     this.actionDialog = false;
    // }

    // getNumber(id, value, type) {
    //     if (
    //         this.values &&
    //         !!this.values[id] &&
    //         !!this.values[id][value] &&
    //         !!this.values[id][value][type] &&
    //         !!this.values[id][value][type].count
    //     ) {
    //         if (this.values[id][value][type].pendingValidation) {
    //             return (
    //                 this.values[id][value][type].pendingValidation +
    //                 "/" +
    //                 this.values[id][value][type].count
    //             );
    //         }
    //         return this.values[id][value][type].count;
    //     }
    //     return 0;
    // }

    onDateRangeChanged(data) {
        this.startDate = data.begin;
        this.endDate = data.end;
    }

    dateRange = {
        begin: null,
        end: null,
    };

    formatMonth(date) {
        const fDate = date.substring(0, 7);
        return fDate;
    }

    // nextDate(date) {
    //     return moment(date, "YYYY-MM").add(1, "M").format("YYYY-MM");
    // }

    // isEndIteration(date) {
    //     return moment(date, "YYYY-MM").isSameOrBefore(
    //         moment(this.formatMonth(this.endDate), "YYYY-MM"),
    //     );
    // }

    // optionContain(option) {
    //     return !!this.optionsSelected.filter((x) => x === option).length;
    // }

    // taxesSelectedContain(taxesSelected, tax) {
    //     return !!taxesSelected.filter((x) => x === tax).length;
    // }

    translateOption(option) {
        switch (option) {
            case "EXPENSE":
                return "Achats";
            case "SALES":
                return "Ventes";
            case "PAY_SLIP":
                return "Payes";
            case "VAT":
                return "TVA";
            case "BANK_TRANSACTION":
                return "Banques";
        }
    }

    // getAuditReportStatusPercent(id) {
    //     if (this.selectedView === "finalization") {
    //         if (
    //             this.auditReportMonitoring[id]?.count &&
    //             (this.auditReportMonitoring[id]?.doneCount !== null ||
    //                 this.auditReportMonitoring[id]?.doneCount !== undefined)
    //         ) {
    //             return (
    //                 (this.auditReportMonitoring[id].doneCount /
    //                     this.auditReportMonitoring[id].count) *
    //                 100
    //             );
    //         } else {
    //             return 0;
    //         }
    //     }
    // }

    // getAuditReportStatus(id) {
    //     if (this.selectedView === "finalization") {
    //         if (
    //             this.auditReportMonitoring[id]?.count &&
    //             (this.auditReportMonitoring[id]?.doneCount !== null ||
    //                 this.auditReportMonitoring[id]?.doneCount !== undefined)
    //         ) {
    //             return (
    //                 this.auditReportMonitoring[id].doneCount +
    //                 "/" +
    //                 this.auditReportMonitoring[id].count
    //             );
    //         } else {
    //             return "Pas de données pour la période recherchée";
    //         }
    //     }
    // }

    // getProgressColor(id) {
    //     if (this.selectedView === "finalization") {
    //         if (
    //             this.auditReportMonitoring[id]?.count &&
    //             (this.auditReportMonitoring[id]?.doneCount !== null ||
    //                 this.auditReportMonitoring[id]?.doneCount !== undefined)
    //         ) {
    //             if (this.auditReportMonitoring[id]?.doneCount === 0) {
    //                 return this.red;
    //             }
    //             if (
    //                 this.auditReportMonitoring[id]?.doneCount ===
    //                 this.auditReportMonitoring[id]?.count
    //             ) {
    //                 return this.green;
    //             } else {
    //                 return "primary";
    //             }
    //         } else {
    //             return this.grey;
    //         }
    //     }
    // }

    getValueForItem(itemId) {
        if (!this.values) {
            return null;
        }
        return this.values[itemId];
    }

    getTaxesMonitoringItem(companyId) {
        if (!this.taxesMonitoring) {
            return null;
        }
        return this.taxesMonitoring[companyId];
    }
}

export default toNative(AccountingClientMonitoring);
</script>
<style lang="scss">
.avatarHover:hover {
    margin: 20px;
    border: 2px white solid;
}
</style>
